import apiService from './api.service';

/**
 * Publish the operator-owned flags of a fixture to All Fixtures.
 *
 * TWO FLAGS, ONE ROW: `is_live` (on air) and, since OPUS-1521, `is_nle` (the
 * non-linear-editing workflow that publishes graphics commands to /topic/nle).
 * NLE has no meaning outside a Live workflow, so the server enforces
 * is_nle => is_live and clears NLE when Live goes off.
 *
 * The Live toggle used to be purely local — it wrote `is_live_enabled` to
 * localStorage and gated Drive's own workflow. Nothing outside this browser
 * ever learned that a match had gone on air, so Aether's Live column could
 * never reflect it.
 *
 * WHY DWS DIRECTLY, AND NOT THE USUAL DDS PASSTHROUGH.
 * Drive's other writes go through DDS's sendPatchesFromDrive, which forwards to
 * DWS's GENERIC /webhook/patch pipeline. That route writes a patch document and
 * never runs the all_fixtures service — so it emits no change notice, which
 * means Aether would not update until someone pressed Refresh, and the whole
 * point of this is that it does. It also skips optimistic concurrency and
 * records no operator attribution.
 *
 * The dedicated PATCH /all-fixtures/:fixtureId does all three: it validates,
 * enforces expected_revision, attributes the change to the signed-in operator,
 * and publishes a `live_updated` notice that reaches Aether over the
 * seasonFixtureResultsUpdated subscription.
 *
 * REQUIRES `REACT_APP_DWS_URL`. Where it is unset this degrades to a no-op with
 * a reason rather than throwing on every toggle — Drive's own Live workflow is
 * unaffected either way, since that is local state and always has been.
 */

const dwsBaseUrl = () => {
  const raw = process.env.REACT_APP_DWS_URL;
  return raw ? String(raw).replace(/\/+$/, '') : null;
};

export const isLivePublishingConfigured = () => Boolean(dwsBaseUrl());

/** Public read — no actor required (see DWS routes/allFixtures.js). */
async function readFixture(fixtureId) {
  const res = await apiService.request(
    `/all-fixtures/${encodeURIComponent(fixtureId)}`,
    { baseUrl: dwsBaseUrl() },
  );
  return res?.fixture ?? res?.data ?? res ?? null;
}

/** Fields this module may publish. Named so a typo is a test failure, not a 400. */
export const FLAGS = Object.freeze({ LIVE: 'is_live', NLE: 'is_nle' });

async function patchFlags(fixtureId, flags, expectedRevision) {
  return apiService.request(`/all-fixtures/${encodeURIComponent(fixtureId)}`, {
    method: 'PATCH',
    baseUrl: dwsBaseUrl(),
    body: JSON.stringify({ ...flags, expected_revision: expectedRevision }),
  });
}

/** Does the stored row already hold every flag the caller asked for? */
function alreadySatisfied(fixture, desired) {
  return Object.entries(desired)
    .every(([field, want]) => Boolean(fixture[field]) === Boolean(want));
}

/**
 * Set one or both operator flags on ONE fixture, in ONE patch.
 *
 * WHY A PARTIAL OBJECT AND NOT TWO CALLS. Turning Live off must also clear NLE.
 * Two sequential PATCHes would burn two revisions, emit two notices, race each
 * other on the second's expected_revision, and leave a window where the row
 * reads { is_live:false, is_nle:true } — the exact state the invariant forbids.
 *
 * NOTE the caller does NOT have to send is_nle:false when turning Live off. DWS
 * cascades it server-side, and sending it explicitly would (a) be rejected as an
 * unknown_field by any DWS that has not shipped OPUS-1521 yet, breaking the Live
 * toggle outright, and (b) report a phantom change on rows written before the
 * field existed. Send only what you mean; let the server keep its own invariant.
 *
 * Never throws: a failure to publish must not block the operator's own workflow,
 * which is what they are actually looking at.
 *
 * @param {string} fixtureId
 * @param {{is_live?: boolean, is_nle?: boolean}} desired
 * @returns {Promise<{ok: boolean, reason?: string, error?: any}>}
 */
export async function setFixtureFlags(fixtureId, desired) {
  if (!fixtureId) return { ok: false, reason: 'no_fixture' };
  if (!dwsBaseUrl()) return { ok: false, reason: 'not_configured' };

  const fields = Object.keys(desired || {});
  if (!fields.length) return { ok: false, reason: 'no_flags' };

  try {
    const fixture = await readFixture(fixtureId);
    if (!fixture) return { ok: false, reason: 'not_found' };

    // Arming NLE on a fixture that will not be live is rejected by DWS. The
    // server is the guarantee; this stops a client bug ever asking, and turns a
    // 400 into a reason the caller can act on.
    const nextIsLive = Object.prototype.hasOwnProperty.call(desired, FLAGS.LIVE)
      ? Boolean(desired[FLAGS.LIVE])
      : Boolean(fixture.is_live);
    if (desired[FLAGS.NLE] === true && !nextIsLive) {
      return { ok: false, reason: 'nle_requires_live' };
    }

    // Already in the requested state — do not burn a revision, and do not emit a
    // notice that would make every subscriber re-read for nothing. EVERY flag
    // must match: if one does and one does not, the whole object still goes.
    if (alreadySatisfied(fixture, desired)) return { ok: true, reason: 'already_set' };

    try {
      await patchFlags(fixtureId, desired, fixture.revision);
      return { ok: true };
    } catch (err) {
      // 409 means somebody else wrote first. Re-read and try once with the
      // revision they left behind; a second conflict is left alone rather than
      // looped, because the other writer is evidently active.
      if (err?.status === 409 || /409/.test(String(err?.message || ''))) {
        const fresh = await readFixture(fixtureId);
        if (!fresh) return { ok: false, reason: 'not_found' };
        if (alreadySatisfied(fresh, desired)) return { ok: true, reason: 'already_set' };
        await patchFlags(fixtureId, desired, fresh.revision);
        return { ok: true, reason: 'retried' };
      }
      throw err;
    }
  } catch (error) {
    console.warn(
      `[allFixturesLive] could not publish ${fields.join('/')}:`,
      error?.message || error,
    );
    return { ok: false, reason: 'request_failed', error };
  }
}

/** Set (or clear) the on-air flag. DWS clears is_nle for us when this goes off. */
export const setFixtureLive = (fixtureId, isLive) =>
  setFixtureFlags(fixtureId, { [FLAGS.LIVE]: isLive });

/** Arm (or disarm) the NLE workflow. Only valid while the fixture is live. */
export const setFixtureNle = (fixtureId, isNle) =>
  setFixtureFlags(fixtureId, { [FLAGS.NLE]: isNle });

/**
 * Read the on-air flag back off the server.
 *
 * THE HAZARD THIS CLOSES: Drive's Live toggle was seeded from
 * localStorage and only ever WRITTEN — nothing read it back. Two Drives on the
 * same fixture therefore held independent booleans over one shared row, and the
 * writes are last-one-wins. Operator B flipping their toggle off wrote
 * is_live:false and took the match off air in Aether while operator A's screen
 * still said Live. Neither UI could see the other, and nothing on screen was
 * wrong enough to notice.
 *
 * Returns { ok, isLive, isNle } so a caller can reconcile. Never throws, and
 * reports `unknown` rather than guessing — a failed read must not be read as
 * "off air", because acting on that guess is exactly the silent take-off-air
 * this exists to prevent.
 *
 * @returns {Promise<{ok: boolean, isLive?: boolean, isNle?: boolean,
 *                    revision?: number, reason?: string}>}
 */
export async function getFixtureFlags(fixtureId) {
  if (!fixtureId) return { ok: false, reason: 'no_fixture' };
  if (!dwsBaseUrl()) return { ok: false, reason: 'not_configured' };
  try {
    const fixture = await readFixture(fixtureId);
    // A fixture with no all_fixtures row is not "off air" — it is unknown, and
    // the caller must leave the operator's own state alone.
    if (!fixture) return { ok: false, reason: 'not_found' };
    return {
      ok: true,
      isLive: Boolean(fixture.is_live),
      // Absent means off. Every row written before OPUS-1521 has no is_nle key,
      // and a DWS older than it serves none — both must read as "not armed",
      // never as unknown.
      isNle: Boolean(fixture.is_nle),
      revision: fixture.revision,
    };
  } catch (error) {
    console.warn('[allFixturesLive] could not read flags:', error?.message || error);
    return { ok: false, reason: 'request_failed' };
  }
}

/** Live-only view, kept so existing callers and their tests are untouched. */
export async function getFixtureLive(fixtureId) {
  const res = await getFixtureFlags(fixtureId);
  return res.ok ? { ok: true, isLive: res.isLive } : res;
}

export default setFixtureLive;
